import { useState, useEffect, useCallback } from "react";
import inventoryApi from "./inventoryApi";
import MonthlyVerificationModal from "./MonthlyVerificationModal";

// ─── helpers ────────────────────────────────────────────────────────────────

function stockStatus(item) {
  if (!item.assigned_quantity || item.remaining_quantity === 0)
    return { label: "Out of stock", color: "#A32D2D", bg: "#FCEBEB", dot: "#E24B4A" };
  const pct = item.remaining_quantity / item.assigned_quantity;
  if (pct <= 0.15)
    return { label: "Low stock", color: "#993C1D", bg: "#FAECE7", dot: "#D85A30" };
  return { label: "Available", color: "#3B6D11", bg: "#EAF3DE", dot: "#639922" };
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit",
  });
}

function bagStatusStyle(status) {
  const s = (status || "").toUpperCase();
  if (s === "ACTIVE")   return { color: "#3B6D11", bg: "#EAF3DE" };
  if (s === "CLOSED")   return { color: "#888",    bg: "#f0f0f0" };
  if (s === "PENDING")  return { color: "#7A5200", bg: "#FFF4D6" };
  return { color: "#555", bg: "#f5f5f5" };
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Spinner({ label = "Loading…" }) {
  return (
    <div style={styles.loaderWrap}>
      <div style={styles.spinner} />
      <span style={{ fontSize: 13, color: "#888" }}>{label}</span>
    </div>
  );
}

function StatCard({ icon, value, label, accent = "#1D9E75" }) {
  return (
    <div style={styles.statCard}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <p style={{ ...styles.statVal, color: accent }}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

function StatusPill({ item }) {
  const s = stockStatus(item);
  return (
    <span style={{ ...styles.pill, background: s.bg, color: s.color }}>
      <span style={{ ...styles.dot, background: s.dot }} />
      {s.label}
    </span>
  );
}

// ── Bag Cards section ────────────────────────────────────────────────────────
// Shows one card per bag with a "Monthly Verification" button.
// Bags are fetched independently using inventoryApi.getBags({ technician_id }).

function BagsSection({ technicianId, onVerify }) {
  const [bags,    setBags]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!technicianId) return;
    setLoading(true);
    // Pass technician_id as a number so the API filter matches correctly
    const tid = Number(technicianId);
    inventoryApi
      .getBags({ technician_id: tid })
      .then((res) => {
        // API may return paginated { results: [] } or a plain array
        const list = Array.isArray(res) ? res : (res.results ?? []);
        // Client-side guard: keep only bags belonging to this technician
        const mine = list.filter(
          (b) => Number(b.current_technician) === tid
        );
        setBags(mine);
      })
      .catch(() => setError("Could not load bags."))
      .finally(() => setLoading(false));
  }, [technicianId]);

  if (loading) {
    return (
      <div style={styles.bagsSection}>
        <p style={styles.bagsSectionTitle}>📦 Inventory Bags</p>
        <Spinner label="Loading bags…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.bagsSection}>
        <p style={styles.bagsSectionTitle}>📦 Inventory Bags</p>
        <span style={{ fontSize: 12, color: "#A32D2D" }}>{error}</span>
      </div>
    );
  }

  if (bags.length === 0) {
    return (
      <div style={styles.bagsSection}>
        <p style={styles.bagsSectionTitle}>📦 Inventory Bags</p>
        <span style={{ fontSize: 12, color: "#aaa" }}>No bags assigned.</span>
      </div>
    );
  }

  return (
    <div style={styles.bagsSection}>
      <p style={styles.bagsSectionTitle}>📦 Inventory Bags — Monthly Verification</p>
      <div style={styles.bagsGrid}>
        {bags.map((bag) => {
          const bs    = bagStatusStyle(bag.status);
          // bag_label comes directly from API e.g. "Bag 6/2026 - abey"
          const label = bag.bag_label ?? bag.bag_name ?? bag.name ?? `Bag #${bag.id}`;
          const month = bag.month
            ? new Date(0, bag.month - 1).toLocaleString("en", { month: "long" })
            : null;
          const itemCount = bag.inventory_items?.length ?? 0;
          return (
            <div key={bag.id} style={styles.bagCard}>
              <div style={styles.bagCardTop}>
                <div>
                  <span style={styles.bagName}>{label}</span>
                  {month && bag.year && (
                    <span style={styles.bagMeta}>
                      {month} {bag.year} · {itemCount} item{itemCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <span style={{ ...styles.bagStatusPill, background: bs.bg, color: bs.color }}>
                  {bag.status ?? "—"}
                </span>
              </div>
              <button
                style={styles.verifyBtn}
                onClick={() => onVerify(bag.id, label)}
              >
                📋 Monthly Verification
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Items table ───────────────────────────────────────────────────────────────

function ItemsTable({ items, type, search, onSearch, onViewLogs }) {
  const filtered = items.filter(
    (i) =>
      i.category === type &&
      i.item_name.toLowerCase().includes(search.toLowerCase())
  );

  const isCapital  = type === "Capital";
  const accent     = isCapital ? "#185FA5" : "#1D9E75";
  const accentBg   = isCapital ? "#E6F1FB" : "#E1F5EE";
  const accentText = isCapital ? "#185FA5" : "#0F6E56";

  return (
    <div style={styles.section}>
      <div style={styles.sectionHead}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#222" }}>
            {isCapital ? "Capital items" : "Consumable items"}
          </span>
          <span style={{ ...styles.badge, background: accentBg, color: accentText }}>
            {filtered.length}
          </span>
        </div>
        <input
          type="text"
          placeholder={`Search ${type.toLowerCase()}s…`}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          style={styles.searchBox}
        />
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: "25%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "19%" }} />
          </colgroup>
          <thead>
            <tr style={styles.thead}>
              {["Item name", "Assigned", "Used", "Remaining", "Status", "Logs"].map(
                (h) => <th key={h} style={styles.th}>{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={styles.emptyCell}>No items found</td>
              </tr>
            ) : filtered.map((item) => (
              <tr key={item.item_id} style={styles.row}>
                <td style={{ ...styles.td, fontWeight: 500 }}>{item.item_name}</td>
                <td style={styles.td}>{item.assigned_quantity}</td>
                <td style={styles.td}>{item.used_quantity}</td>
                <td style={{ ...styles.td, fontWeight: 600, color: accent }}>
                  {item.remaining_quantity}
                </td>
                <td style={styles.td}>
                  <StatusPill item={item} />
                </td>
                <td style={styles.td}>
                  {item.usage_logs?.length > 0 ? (
                    <button
                      style={styles.logsBtn}
                      onClick={() => onViewLogs(item)}
                    >
                      View logs ({item.usage_logs.length})
                    </button>
                  ) : (
                    <span style={{ color: "#ccc", fontSize: 13 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Logs sub-modal ────────────────────────────────────────────────────────────

function LogsModal({ item, onClose }) {
  if (!item) return null;
  const logs = item.usage_logs || [];

  return (
    <div style={styles.logsOverlay} onClick={onClose}>
      <div style={styles.logsModal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.logsHeader}>
          <div>
            <p style={styles.logsTitle}>Usage logs — {item.item_name}</p>
            <p style={styles.logsMeta}>
              {item.category} &nbsp;·&nbsp; {item.unit} &nbsp;·&nbsp;
              Assigned {item.assigned_quantity} &nbsp;·&nbsp;
              Used {item.used_quantity} &nbsp;·&nbsp;
              Remaining{" "}
              <strong style={{ color: "#1D9E75" }}>{item.remaining_quantity}</strong>
            </p>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close logs">×</button>
        </div>
        <div style={styles.logsBody}>
          {logs.length === 0 ? (
            <div style={{ ...styles.loaderWrap, padding: "2.5rem" }}>
              <span style={{ fontSize: 13, color: "#aaa" }}>No usage logs recorded.</span>
            </div>
          ) : (
            <table style={{ ...styles.table, fontSize: 12 }}>
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "24%" }} />
              </colgroup>
              <thead>
                <tr style={styles.thead}>
                  {["Date & time", "Camp", "Qty used", "Performed by", "Remarks"].map(
                    (h) => <th key={h} style={styles.th}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={styles.row}>
                    <td style={styles.td}>
                      <span style={{ display: "block", fontWeight: 500 }}>{fmtDate(log.used_at)}</span>
                      <span style={{ color: "#aaa", fontSize: 11 }}>{fmtTime(log.used_at)}</span>
                    </td>
                    <td style={styles.td}>{log.camp_name || `Camp ${log.camp_id}`}</td>
                    <td style={{ ...styles.td, fontWeight: 700, color: "#1D9E75" }}>{log.quantity_used}</td>
                    <td style={{ ...styles.td, color: "#666" }}>{log.created_by || "—"}</td>
                    <td style={{ ...styles.td, color: "#888" }}>{log.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function InventorySummaryModal({ technicianId, onClose }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState({ Consumable: "", Capital: "" });
  const [logsItem,  setLogsItem]  = useState(null);
  const [verifyBag, setVerifyBag] = useState(null); // { bagId, bagLabel } | null

  useEffect(() => {
    setLoading(true);
    setError(null);
    inventoryApi
      .fetchInventorySummary(technicianId)
      .then(setData)
      .catch(() => setError("Failed to load inventory summary. Please try again."))
      .finally(() => setLoading(false));
  }, [technicianId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !logsItem && !verifyBag) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, logsItem, verifyBag]);

  const handleSearch = useCallback((type, val) => {
    setSearch((s) => ({ ...s, [type]: val }));
  }, []);

  const handleVerify = useCallback((bagId, bagLabel) => {
    setVerifyBag({ bagId, bagLabel });
  }, []);

  const totalItems = data?.items?.length ?? 0;
  const lowOrOut   = data?.items?.filter((i) => {
    const pct = i.remaining_quantity / (i.assigned_quantity || 1);
    return pct <= 0.15;
  }).length ?? 0;

  return (
    <>
      {/* backdrop */}
      <div style={styles.backdrop} onClick={onClose} />

      {/* main modal */}
      <div
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Inventory Summary Dashboard"
      >
        {/* header */}
        <div style={styles.header}>
          <div>
            <p style={styles.title}>Inventory Summary</p>
            <p style={styles.subtitle}>Technician #{technicianId}</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* body */}
        <div style={styles.body}>
          {loading && <Spinner label="Loading inventory…" />}

          {error && (
            <div style={styles.errorBox}>
              <span>⚠️</span> {error}
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* stat cards */}
              <div style={styles.statsGrid}>
                <StatCard icon="🗂️" value={totalItems} label="Total items" />
                <StatCard icon="🧴" value={data.summary.total_consumable_items} label="Consumables" />
                <StatCard icon="🔧" value={data.summary.total_capital_items} label="Capital items" accent="#185FA5" />
                <StatCard icon="⚠️" value={lowOrOut} label="Low / out of stock" accent="#993C1D" />
              </div>

              {/* ── Bags + Monthly Verification buttons ── */}
              <BagsSection technicianId={technicianId} onVerify={handleVerify} />

              {/* item tables */}
              <ItemsTable
                items={data.items}
                type="Consumable"
                search={search.Consumable}
                onSearch={(v) => handleSearch("Consumable", v)}
                onViewLogs={setLogsItem}
              />
              <ItemsTable
                items={data.items}
                type="Capital"
                search={search.Capital}
                onSearch={(v) => handleSearch("Capital", v)}
                onViewLogs={setLogsItem}
              />
            </>
          )}
        </div>
      </div>

      {/* logs sub-modal */}
      {logsItem && (
        <LogsModal item={logsItem} onClose={() => setLogsItem(null)} />
      )}

      {/* Monthly Verification sub-modal */}
      {verifyBag && (
        <MonthlyVerificationModal
          bagId={verifyBag.bagId}
          bagLabel={verifyBag.bagLabel}
          onClose={() => setVerifyBag(null)}
        />
      )}
    </>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const styles = {
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.45)",
    zIndex: 50,
  },
  modal: {
    position: "fixed",
    top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 51,
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
    width: "min(96vw, 1060px)",
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem 1.5rem",
    borderBottom: "1px solid #f0f0f0",
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: 16, fontWeight: 600, color: "#111" },
  subtitle: { margin: "2px 0 0", fontSize: 12, color: "#999" },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 26, lineHeight: 1,
    cursor: "pointer", color: "#bbb",
    padding: "0 4px", flexShrink: 0,
  },
  body: { overflowY: "auto", padding: "1.25rem 1.5rem", flex: 1 },
  loaderWrap: {
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: 12, padding: "3rem",
  },
  spinner: {
    width: 30, height: 30,
    border: "2.5px solid #eee",
    borderTopColor: "#1D9E75",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
  errorBox: {
    display: "flex", alignItems: "center", gap: 8,
    background: "#FCEBEB", color: "#A32D2D",
    borderRadius: 8, padding: "0.75rem 1rem", fontSize: 13,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10, marginBottom: "1.25rem",
  },
  statCard: {
    background: "#f9f9f8", borderRadius: 10,
    padding: "0.85rem 1rem", textAlign: "center",
  },
  statVal: { margin: "4px 0 2px", fontSize: 22, fontWeight: 700 },
  statLabel: { margin: 0, fontSize: 11, color: "#999" },

  // ── Bags section ──
  bagsSection: {
    background: "#f9f9fb",
    border: "1px solid #ece9ff",
    borderRadius: 12,
    padding: "1rem 1.25rem",
    marginBottom: "1.5rem",
  },
  bagsSectionTitle: {
    margin: "0 0 12px",
    fontSize: 13,
    fontWeight: 700,
    color: "#3D2FA0",
  },
  bagsGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  bagCard: {
    background: "#fff",
    border: "1.5px solid #ddd9f5",
    borderRadius: 10,
    padding: "10px 14px",
    minWidth: 210,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  bagCardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  bagName: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#222",
  },
  bagMeta: {
    display: "block",
    fontSize: 11,
    color: "#999",
    marginTop: 2,
  },
  bagStatusPill: {
    fontSize: 10,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 99,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    flexShrink: 0,
  },
  verifyBtn: {
    fontSize: 12,
    padding: "6px 12px",
    borderRadius: 7,
    border: "1.5px solid #7B61FF",
    color: "#5B41CC",
    background: "#F4F1FF",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
    alignSelf: "flex-start",
  },

  // item tables
  section: { marginBottom: "1.75rem" },
  sectionHead: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10, flexWrap: "wrap", gap: 8,
  },
  badge: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99 },
  searchBox: {
    border: "1px solid #e5e5e5", borderRadius: 8,
    padding: "5px 10px", fontSize: 12, outline: "none",
    width: 200, color: "#333",
  },
  tableWrap: { border: "1px solid #f0f0f0", borderRadius: 10, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, tableLayout: "fixed" },
  thead: { background: "#fafaf9" },
  th: {
    padding: "8px 12px", textAlign: "left",
    fontSize: 11, fontWeight: 600, color: "#bbb",
    textTransform: "uppercase", letterSpacing: "0.04em",
    borderBottom: "1px solid #f0f0f0",
  },
  row: { borderBottom: "1px solid #f7f7f6" },
  td: { padding: "9px 12px", color: "#333", verticalAlign: "middle" },
  emptyCell: { textAlign: "center", color: "#ccc", padding: "2rem", fontSize: 13 },
  pill: {
    display: "inline-flex", alignItems: "center",
    gap: 5, fontSize: 11, fontWeight: 600,
    padding: "3px 8px", borderRadius: 99,
  },
  dot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
  logsBtn: {
    fontSize: 12, padding: "4px 10px", borderRadius: 6,
    border: "1px solid #1D9E75", color: "#0F6E56",
    background: "transparent", cursor: "pointer", fontWeight: 500,
  },

  // logs sub-modal
  logsOverlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 60,
    display: "flex", alignItems: "center",
    justifyContent: "center", padding: "1rem",
  },
  logsModal: {
    background: "#fff", borderRadius: 12,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    width: "min(96vw, 780px)", maxHeight: "80vh",
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  logsHeader: {
    display: "flex", justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "1rem 1.25rem", borderBottom: "1px solid #f0f0f0", flexShrink: 0,
  },
  logsTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: "#111" },
  logsMeta: { margin: "4px 0 0", fontSize: 12, color: "#aaa" },
  logsBody: { overflowY: "auto", flex: 1 },
};